import { REGISTRATION_PLAN_ID_MAX_LENGTH, type RegistrationPlanDetails } from '@galapagos/shared';
import type { SignupSponsorFeatures } from '../services/signup-api/signup-api';
import { nextSignupStep, type SignupPath, type SignupStepId } from '../steps';

/**
 * The legacy signup's other flow versions. A link that names one of these in
 * `flow=` asks for that flow instead of the health-plan one; any other value
 * (or none) leaves a health-plan sponsor on the health-plan flow, as
 * production does when the named flow is not one the sponsor runs.
 */
export const OTHER_SIGNUP_FLOWS: readonly string[] = [
  'lighter_v1',
  'saml_v1',
  'unknown_client',
  'original',
];

export interface PlanDetailsTrigger {
  /** The sponsor's health-plan switch; undefined while the sponsor read is pending or failed. */
  healthPlan: boolean | undefined;
  /** The link's `flow=` value, already trimmed and lower-cased. */
  flow: string | undefined;
}

/** Whether the member is asked for their plan details after creating the account. */
export function showsPlanDetails({ healthPlan, flow }: PlanDetailsTrigger): boolean {
  if (healthPlan !== true) return false;
  return flow === undefined || !OTHER_SIGNUP_FLOWS.includes(flow);
}

/**
 * Where the flow goes once the code screen has signed the member in. A link
 * with no employer has no sponsor yet at this point, so it keeps its own order
 * and never asks for plan details.
 */
export function stepAfterAccount({
  features,
  flow,
  path,
}: {
  features: SignupSponsorFeatures | undefined;
  flow: string | undefined;
  path: SignupPath;
}): SignupStepId {
  if (path === 'no-employer') {
    const next = nextSignupStep('code', path);
    if (next === null) throw new Error('The code step must be followed by another step');
    return next;
  }
  if (showsPlanDetails({ healthPlan: features?.healthPlan, flow })) return 'plan-details';
  const next = nextSignupStep('plan-details');
  if (next === null) throw new Error('The plan details step must be followed by another step');
  return next;
}

/** The plan details as the flow keeps them: trimmed, with no group number as null. */
export interface SignupPlanDetails {
  memberId: string;
  groupNumber: string | null;
}

/**
 * Normalises the two fields. Null when the member ID is blank, which the
 * screen shows as its required error; the group number is optional.
 */
export function planDetailsFromInput(
  memberId: string,
  groupNumber: string,
): SignupPlanDetails | null {
  const id = memberId.trim();
  if (id === '') return null;
  const group = groupNumber.trim();
  return { memberId: id, groupNumber: group === '' ? null : group };
}

export interface PlanDetailsInputErrors {
  memberId: 'required' | 'tooLong' | null;
  groupNumber: 'tooLong' | null;
}

// Measured after trimming, with string length, so the screen rejects exactly
// what the profile save's schema would reject with a 400.
const tooLong = (value: string) => value.trim().length > REGISTRATION_PLAN_ID_MAX_LENGTH;

/** The field errors Continue shows; both null when the answers can be kept. */
export function planDetailsInputErrors(
  memberId: string,
  groupNumber: string,
): PlanDetailsInputErrors {
  return {
    memberId: memberId.trim() === '' ? 'required' : tooLong(memberId) ? 'tooLong' : null,
    groupNumber: tooLong(groupNumber) ? 'tooLong' : null,
  };
}

/**
 * The stored plan details in the profile save's wire shape, with no group
 * number key when there is none; undefined when the member gave none.
 */
export function planDetailsWrite(
  planDetails: SignupPlanDetails | null,
): RegistrationPlanDetails | undefined {
  if (planDetails === null) return undefined;
  const { memberId, groupNumber } = planDetails;
  return groupNumber === null
    ? { planMemberId: memberId }
    : { planMemberId: memberId, planGroupNo: groupNumber };
}
